import { useState } from 'react';

import { InfoModal } from '@backrs_ui/common-ui/src/atoms/InfoModal';
import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Button, Typography, Box, Dialog, Theme, SxProps } from '@mui/material';

type ConfirmDeleteVariant = 'destructive' | 'warning';

type ConfirmDeleteVariantConfig = Readonly<{
  iconName: AppIcons;
  iconColor: ColorToken;
  iconBg: ColorToken;
  buttonSx?: SxProps<Theme>;
}>;

type ConfirmDeleteProps = Readonly<{
  openDialog: boolean;
  setOpenDialog: (open: boolean) => void;
  onSuccess?: (deletedId: number) => void;
  headerText: string;
  bodyText: string;
  handleDelete: () => void;
  confirmButtonText?: string;
  headerInLineWithIcon?: boolean;
  variant?: ConfirmDeleteVariant;
}>;

export const ConfirmDelete = ({
  openDialog,
  setOpenDialog,
  headerText,
  bodyText,
  handleDelete,
  confirmButtonText = 'Delete',
  headerInLineWithIcon = true,
  variant = 'destructive',
}: ConfirmDeleteProps) => {
  const config = VARIANT_CONFIG[variant];
  const [error, setError] = useState<string | null>(null);

  const handleCancel = () => {
    setOpenDialog(false);
    setError(null);
  };

  return (
    <Dialog open={openDialog} onClose={handleCancel}>
      <InfoModal
        featuredIcon={{
          iconName: config.iconName,
          size: 'lg',
          color: config.iconColor,
          shape: 'round',
          backgroundColor: config.iconBg,
          border: false,
        }}
        headerInLineWithIcon={headerInLineWithIcon}
        headerContent={
          <Box sx={headerContainerSx}>
            <Typography variant="text-lg-semibold">{headerText}</Typography>
            <Typography variant="text-sm-regular">{bodyText}</Typography>
          </Box>
        }
        bodyContent={
          <Box sx={bodyContainerSx}>
            <Box sx={buttonContainerSx}>
              <Button
                id="confirm-delete-cancel-button"
                onClick={handleCancel}
                hierarchy="Secondary gray"
                size="lg"
              >
                Cancel
              </Button>
              <Button
                id="confirm-delete-confirm-button"
                onClick={handleDelete}
                hierarchy="Primary"
                size="lg"
                sx={config.buttonSx}
              >
                {confirmButtonText}
              </Button>
            </Box>
            {error && (
              <Typography
                variant="text-md-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
                mt={3}
              >
                {error}
              </Typography>
            )}
          </Box>
        }
        onClose={handleCancel}
        open={openDialog}
        width="xs"
      />
    </Dialog>
  );
};

// Shared Styles
const headerContainerSx: SxProps<Theme> = {
  zIndex: 100,
  position: 'relative',
};

const bodyContainerSx: SxProps<Theme> = {
  zIndex: 100,
  position: 'relative',
};

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  alignSelf: 'flex-end',
  justifyContent: 'flex-end',
});

const deleteButtonSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Error/700'],
  backgroundColor: backrs.colors['Colors/Background/bg-error-solid'],
  color: backrs.colors['Colors/Base/White'],
  '&:hover': {
    backgroundColor: backrs.colors['Error/700'],
    transform: 'none',
    boxShadow: 'none',
  },
});

const VARIANT_CONFIG: Record<ConfirmDeleteVariant, ConfirmDeleteVariantConfig> = {
  destructive: {
    iconName: 'Trash01',
    iconColor: 'Colors/Text/text-error-primary',
    iconBg: 'Colors/Background/bg-error-secondary',
    buttonSx: deleteButtonSx,
  },
  warning: {
    iconName: 'AlertTriangle',
    iconColor: 'Colors/Foreground/fg-warning-secondary',
    iconBg: 'Colors/Background/bg-warning-secondary',
  },
};
